import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import type { VisualCue } from "../types";
import { CuePicture, cuePictureLabel } from "../launchpad/catalog";
import "./talent-cue.css";
import { readableCueColor } from "../launchpad/color";

export function TalentCue({ cue, width, height, opacity = 1, layer = "front" }: {
  cue: VisualCue;
  width: number;
  height: number;
  opacity?: number;
  layer?: "front" | "behind";
}) {
  const [, updateClock] = useState(0);
  const message = useRef<HTMLDivElement>(null);
  const overlay = useRef<HTMLDivElement>(null);
  const banner = cue.layout === "banner";
  const color = readableCueColor(cue.color);
  const unit = Math.min(width / 1024, height / 600);
  const hasPicture = cue.picture !== "none";
  const textScale = cue.text.length > 90 ? 0.68 : cue.text.length > 45 ? 0.82 : 1;
  const pictureSize = (banner ? 84 : hasPicture && cue.text ? 240 : 320) * unit;
  const gap = (banner ? 24 : 20) * unit;
  const padding = (banner ? 24 : 36) * unit;
  const textSize = (banner ? 36 : 58) * unit * textScale;
  useLayoutEffect(() => {
    const text = message.current;
    if (!text || !overlay.current) return;
    const availableHeight = (banner ? height * 0.48 : height) - padding * 2 -
      (!banner && hasPicture ? pictureSize + gap : 0);
    // Fit unusually long directions and scripts with wide glyphs without truncating their meaning.
    // This runs only when the cue/canvas changes, never in the continuous scrolling loop.
    let low = Math.max(1, 5 * unit), high = textSize;
    text.style.fontSize = `${high}px`;
    if (text.scrollHeight <= availableHeight) return;
    for (let step = 0; step < 9; step++) {
      const size = (low + high) / 2;
      text.style.fontSize = `${size}px`;
      if (text.scrollHeight > availableHeight) high = size;
      else low = size;
    }
    text.style.fontSize = `${low}px`;
  }, [cue.id, cue.text, banner, hasPicture, height, width, unit, pictureSize, gap, padding, textSize]);
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const checkExpiry = () => {
      if (timeout !== undefined) clearTimeout(timeout);
      updateClock((value) => value + 1);
      if (cue.expiresAt !== null && cue.expiresAt > Date.now())
        timeout = setTimeout(checkExpiry, Math.min(2_147_483_647, cue.expiresAt - Date.now()));
    };
    checkExpiry();
    // Suspended tabs may not run timers. Recheck wall-clock expiry before showing a cue again.
    document.addEventListener("visibilitychange", checkExpiry);
    window.addEventListener("pageshow", checkExpiry);
    window.addEventListener("focus", checkExpiry);
    return () => {
      if (timeout !== undefined) clearTimeout(timeout);
      document.removeEventListener("visibilitychange", checkExpiry);
      window.removeEventListener("pageshow", checkExpiry);
      window.removeEventListener("focus", checkExpiry);
    };
  }, [cue.id, cue.expiresAt]);

  if (cue.expiresAt !== null && cue.expiresAt <= Date.now()) return null;
  const style = {
    opacity,
    pointerEvents: opacity === 0 || layer === "behind" ? "none" : "auto",
    "--talent-cue-color": color,
    "--talent-cue-picture": `${pictureSize}px`,
    "--talent-cue-type": `${textSize}px`,
    "--talent-cue-gap": `${gap}px`,
    "--talent-cue-padding": `${padding}px`,
  } as CSSProperties;
  return (
    <div
      ref={overlay}
      className={`talent-cue talent-cue--${banner ? "banner" : "card"}`}
      style={style}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label={cue.text || cuePictureLabel(cue.picture)}
      data-cue-id={cue.id}
      data-layer={layer}
      aria-hidden={opacity === 0 || undefined}
    >
      {hasPicture && <CuePicture id={cue.picture} className="talent-cue-picture" />}
      {cue.text && <div ref={message} className="talent-cue-message" dir="auto">{cue.text}</div>}
    </div>
  );
}
